/**
 * The 20 NAICS sectors. `industries` documents store the sector name verbatim as
 * their `category`, so this union is the canonical category taxonomy across the
 * frontend (filters, category pages) and the Firestore migration script.
 */
export type IndustryCategory =
  | 'Agriculture, Forestry, Fishing and Hunting'
  | 'Mining, Quarrying, and Oil and Gas Extraction'
  | 'Utilities'
  | 'Construction'
  | 'Manufacturing'
  | 'Wholesale Trade'
  | 'Retail Trade'
  | 'Transportation and Warehousing'
  | 'Information'
  | 'Finance and Insurance'
  | 'Real Estate and Rental and Leasing'
  | 'Professional, Scientific, and Technical Services'
  | 'Management of Companies and Enterprises'
  | 'Administrative and Support and Waste Management and Remediation Services'
  | 'Educational Services'
  | 'Health Care and Social Assistance'
  | 'Arts, Entertainment, and Recreation'
  | 'Accommodation and Food Services'
  | 'Other Services (except Public Administration)'
  | 'Public Administration';

/** A sub-category (NAICS subsector) within a category, with its industries. */
export interface IndustrySubcategory {
  subsectorId: string;
  subsectorName: string;
  industries: Industry[];
}

/** A category as stored in the Firestore `categories` collection. */
export interface IndustryCategoryInfo {
  id: string; // slug of the name (doc id)
  name: IndustryCategory;
  sectorIds: string[]; // NAICS sector ids that roll up to this category
  industryCount: number; // industries in this category
}

/** All NAICS sector categories, in canonical sector-id order. */
export const INDUSTRY_CATEGORIES: IndustryCategory[] = [
  'Agriculture, Forestry, Fishing and Hunting',
  'Mining, Quarrying, and Oil and Gas Extraction',
  'Utilities',
  'Construction',
  'Manufacturing',
  'Wholesale Trade',
  'Retail Trade',
  'Transportation and Warehousing',
  'Information',
  'Finance and Insurance',
  'Real Estate and Rental and Leasing',
  'Professional, Scientific, and Technical Services',
  'Management of Companies and Enterprises',
  'Administrative and Support and Waste Management and Remediation Services',
  'Educational Services',
  'Health Care and Social Assistance',
  'Arts, Entertainment, and Recreation',
  'Accommodation and Food Services',
  'Other Services (except Public Administration)',
  'Public Administration',
];

export interface Industry {
  id: string;
  name: string; // microSubsectorName — the industry leaf
  category: IndustryCategory; // sectorName — the main NAICS category
  description: string;
  growthRate: number;
  marketSize: string;
  competitionLevel: 'Low' | 'Medium' | 'High';
  trending: boolean;
  location: string;
  /** Free-form keywords for search/assessment matching (e.g. ['ai','software']). */
  tags?: string[];
  // --- NAICS hierarchy (present on migrated Firestore docs) ---
  naicsCode?: string;
  sectorId?: string;
  sectorName?: string;
  subsectorId?: string;
  subsectorName?: string; // the sub-category within the sector
  microSubsectorId?: string;
  microSubsectorName?: string;
  submicroSubsectorId?: string;
}

export interface BusinessProfile {
  id: string;
  name: string;
  industryId: string;
  /** Human-readable industry name, carried from checkout to brand the business. */
  industryName?: string;
  /** NAICS code of the selected industry — drives federal-compliant cross-promotion. */
  naicsCode?: string;
  size: 'Small' | 'Medium' | 'Large' | 'Enterprise';
  goals: string[];
  teamMembers: string[];
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: 'monthly' | 'yearly';
  features: string[];
  businessSize: 'Small' | 'Medium' | 'Large' | 'Enterprise';
}

export interface Subscription {
  id: string;
  planId: string;
  industryId: string;
  status: 'active' | 'cancelled' | 'expired' | 'trial';
  startDate: string;
  endDate: string;
}
